/**
 * BLOCK 18 ,  Admin Edit-on-Verify modal.
 *
 * Opens over the Opportunity detail page for a pending_review submission.
 * Presents a compact form of the fields an admin might correct before
 * approving. Saves via PATCH /api/admin/opportunities/:oid/verify-edit
 * which logs a field-level diff to audit_log and optionally rescores the
 * row. Approve/reject are unchanged and remain separate actions.
 */
import { useState } from "react";
import { X, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { errMsg, errCode } from "@/lib/utils";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY",
  "LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH",
  "OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC",
];

const inputCls = "w-full bg-[#0A0D12] border border-white/10 focus:border-emerald-500/60 focus:ring-1 focus:ring-emerald-500/30 rounded-sm px-3 py-2 text-sm text-slate-100 placeholder-slate-600 outline-none transition-colors";
const labelCls = "block text-[10px] uppercase tracking-widest text-slate-500 mb-1";

function Field({ label, children, span = 1 }) {
  return (
    <div className={span === 2 ? "md:col-span-2" : ""}>
      <label className={labelCls}>{label}</label>
      {children}
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <section className="border-t border-white/8 pt-4 mt-4 first:border-t-0 first:pt-0 first:mt-0" data-testid={testid}>
      <h3 className="text-xs uppercase tracking-widest text-emerald-400 mb-3">{title}</h3>
      <div className="grid md:grid-cols-2 gap-3">{children}</div>
    </section>
  );
}

export default function VerifyEditModal({ opp, onClose, onSaved }) {
  const [f, setF] = useState({
    // basics
    title: opp.title ?? "",
    asking_price: opp.asking_price ?? "",
    description: opp.description ?? "",
    listing_url: opp.listing_url ?? "",
    // location
    address: opp.address ?? "",
    city: opp.city ?? "",
    state: opp.state ?? "",
    zip_code: opp.zip_code ?? "",
    county: opp.county ?? "",
    // owner
    owner_name: opp.owner_name ?? "",
    owner_email: opp.owner_email ?? "",
    owner_phone: opp.owner_phone ?? "",
    // asset-specific
    industry: opp.industry ?? "",
    revenue: opp.revenue ?? "",
    cash_flow: opp.cash_flow ?? "",
    units: opp.units ?? "",
    noi: opp.noi ?? "",
    occupancy: opp.occupancy ?? "",
    cap_rate: opp.cap_rate ?? "",
    year_built: opp.year_built ?? "",
    acreage: opp.acreage ?? "",
    zoning: opp.zoning ?? "",
    // distress + motivation
    absentee_owner: !!opp.absentee_owner,
    tax_delinquent: !!opp.tax_delinquent,
    deferred_maintenance: !!opp.deferred_maintenance,
    lien_flag: !!opp.lien_flag,
    seller_financing_flag: !!opp.seller_financing_flag,
    seller_financing_terms: opp.seller_financing_terms ?? "",
    reason_for_selling: opp.reason_for_selling ?? "",
    years_owned: opp.years_owned ?? "",
    assessed_value: opp.assessed_value ?? "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  // Build a diff of ONLY changed fields, coercing numeric strings to numbers
  const buildDiff = () => {
    const num = (v) => (v === "" || v == null ? null : Number(v));
    const changed = {};
    for (const k of Object.keys(f)) {
      let next = f[k];
      // Coerce numeric fields
      if (["asking_price", "revenue", "cash_flow", "noi", "occupancy",
           "cap_rate", "acreage", "assessed_value"].includes(k)) {
        next = num(next);
      } else if (["units", "year_built", "years_owned"].includes(k)) {
        next = next === "" ? null : parseInt(next, 10);
      } else if (typeof next === "string") {
        next = next.trim();
      }
      const prior = opp[k] ?? (typeof f[k] === "boolean" ? false : (typeof f[k] === "number" ? null : ""));
      const priorNorm = typeof prior === "string" ? prior.trim() : prior;
      if (next !== priorNorm && !(next === null && (priorNorm === "" || priorNorm == null))) {
        changed[k] = next;
      }
    }
    return changed;
  };

  const save = async () => {
    const r = await api.patch(`/admin/opportunities/${opp.oid}/verify-edit`, buildDiff());
    const changed = Object.keys(r.data?.diff || {});
    toast.success(
      `Saved ${changed.length} field${changed.length === 1 ? "" : "s"}${
        r.data?.rescored ? " · rescored" : ""
      }`
    );
    onSaved?.(r.data?.opportunity);
    onClose?.();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const diff = buildDiff();
    if (!Object.keys(diff).length) {
      toast.error("No changes to save.");
      return;
    }
    setBusy(true);
    try {
      await save();
    } catch (e) {
      // BLOCK 184 ,  verify-edit also requires a fresh MFA step-up (10
      // min TTL) ,  prompt for the code and retry instead of failing.
      if (errCode(e) === "mfa_step_up_required") {
        const code = window.prompt("This action needs a fresh authenticator code.\nEnter your current 6-digit code to continue:");
        if (!code) {
          toast.message("Cancelled , a fresh code is required to continue.");
          setBusy(false);
          return;
        }
        try {
          await api.post("/mfa/step-up", { code: code.trim() });
          await save();
        } catch (e2) {
          toast.error(errMsg(e2, "Save failed"));
        }
      } else {
        toast.error(errMsg(e, "Save failed"));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="verify-edit-modal">
      <form onSubmit={onSubmit} className="ae-card w-full max-w-3xl my-8 bg-[#05070A]">
        <header className="flex items-center justify-between p-5 border-b border-white/8 sticky top-0 bg-[#05070A] z-10">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-sm bg-amber-500/15 flex items-center justify-center">
              <ShieldCheck className="h-4 w-4 text-amber-400" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-amber-400">Admin · Edit-on-Verify</div>
              <h2 className="font-heading text-lg">Correct submission before approving</h2>
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="verify-edit-close" className="text-slate-500 hover:text-slate-200">
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="p-5 space-y-4">
          <p className="text-xs text-slate-500 leading-relaxed">
            Each save is logged to the audit trail with a field-level diff. Motivation-relevant edits (price, financing, distress signals, assessed value) trigger a rescore. This form does not approve or reject the submission ,  use the Pending Review inbox actions for that.
          </p>

          <Section title="Asset basics" testid="ve-section-basics">
            <Field label="Title" span={2}>
              <input className={inputCls} value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="ve-title" />
            </Field>
            <Field label="Asking price ($)">
              <input type="number" min="0" step="1" className={inputCls} value={f.asking_price} onChange={(e) => set("asking_price", e.target.value)} data-testid="ve-asking-price" />
            </Field>
            <Field label="Listing URL">
              <input type="url" className={inputCls} value={f.listing_url} onChange={(e) => set("listing_url", e.target.value)} data-testid="ve-listing-url" />
            </Field>
            <Field label="Description" span={2}>
              <textarea rows={3} className={inputCls} value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="ve-description" />
            </Field>
          </Section>

          <Section title="Location" testid="ve-section-location">
            <Field label="Street address" span={2}>
              <input className={inputCls} value={f.address} onChange={(e) => set("address", e.target.value)} data-testid="ve-address" />
            </Field>
            <Field label="City">
              <input className={inputCls} value={f.city} onChange={(e) => set("city", e.target.value)} data-testid="ve-city" />
            </Field>
            <Field label="State">
              <select className={inputCls} value={f.state} onChange={(e) => set("state", e.target.value)} data-testid="ve-state">
                <option value="">(none)</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="ZIP">
              <input className={inputCls} value={f.zip_code} onChange={(e) => set("zip_code", e.target.value)} data-testid="ve-zip" />
            </Field>
            <Field label="County">
              <input className={inputCls} value={f.county} onChange={(e) => set("county", e.target.value)} data-testid="ve-county" />
            </Field>
          </Section>

          <Section title="Owner contact" testid="ve-section-owner">
            <Field label="Owner name">
              <input className={inputCls} value={f.owner_name} onChange={(e) => set("owner_name", e.target.value)} data-testid="ve-owner-name" />
            </Field>
            <Field label="Owner email">
              <input type="email" className={inputCls} value={f.owner_email} onChange={(e) => set("owner_email", e.target.value)} data-testid="ve-owner-email" />
            </Field>
            <Field label="Owner phone">
              <input type="tel" className={inputCls} value={f.owner_phone} onChange={(e) => set("owner_phone", e.target.value)} data-testid="ve-owner-phone" />
            </Field>
          </Section>

          {opp.asset_type === "business" && (
            <Section title="Business specifics" testid="ve-section-business">
              <Field label="Industry">
                <input className={inputCls} value={f.industry} onChange={(e) => set("industry", e.target.value)} data-testid="ve-industry" />
              </Field>
              <Field label="Annual revenue ($)">
                <input type="number" min="0" step="1" className={inputCls} value={f.revenue} onChange={(e) => set("revenue", e.target.value)} data-testid="ve-revenue" />
              </Field>
              <Field label="Cash flow / SDE ($)">
                <input type="number" min="0" step="1" className={inputCls} value={f.cash_flow} onChange={(e) => set("cash_flow", e.target.value)} data-testid="ve-cash-flow" />
              </Field>
            </Section>
          )}
          {opp.asset_type === "multifamily" && (
            <Section title="Multifamily specifics" testid="ve-section-multifamily">
              <Field label="Units"><input type="number" min="0" className={inputCls} value={f.units} onChange={(e) => set("units", e.target.value)} data-testid="ve-units" /></Field>
              <Field label="NOI ($)"><input type="number" className={inputCls} value={f.noi} onChange={(e) => set("noi", e.target.value)} data-testid="ve-noi" /></Field>
              <Field label="Occupancy %"><input type="number" step="0.01" className={inputCls} value={f.occupancy} onChange={(e) => set("occupancy", e.target.value)} data-testid="ve-occupancy" /></Field>
              <Field label="Cap rate"><input type="number" step="0.001" className={inputCls} value={f.cap_rate} onChange={(e) => set("cap_rate", e.target.value)} data-testid="ve-cap-rate" /></Field>
              <Field label="Year built"><input type="number" className={inputCls} value={f.year_built} onChange={(e) => set("year_built", e.target.value)} data-testid="ve-year-built" /></Field>
            </Section>
          )}
          {opp.asset_type === "land" && (
            <Section title="Land specifics" testid="ve-section-land">
              <Field label="Acreage"><input type="number" step="0.01" className={inputCls} value={f.acreage} onChange={(e) => set("acreage", e.target.value)} data-testid="ve-acreage" /></Field>
              <Field label="Zoning"><input className={inputCls} value={f.zoning} onChange={(e) => set("zoning", e.target.value)} data-testid="ve-zoning" /></Field>
            </Section>
          )}

          <Section title="Motivation signals" testid="ve-section-motivation">
            <Field label="Reason for selling" span={2}>
              <input className={inputCls} value={f.reason_for_selling} onChange={(e) => set("reason_for_selling", e.target.value)} data-testid="ve-reason-for-selling" />
            </Field>
            <Field label="Years owned"><input type="number" min="0" className={inputCls} value={f.years_owned} onChange={(e) => set("years_owned", e.target.value)} data-testid="ve-years-owned" /></Field>
            <Field label="Assessed value ($)"><input type="number" min="0" step="1" className={inputCls} value={f.assessed_value} onChange={(e) => set("assessed_value", e.target.value)} data-testid="ve-assessed-value" /></Field>
            <div className="md:col-span-2 grid md:grid-cols-2 gap-2">
              {[
                ["absentee_owner", "Absentee owner"],
                ["tax_delinquent", "Tax delinquent"],
                ["deferred_maintenance", "Deferred maintenance"],
                ["lien_flag", "Active lien"],
                ["seller_financing_flag", "Seller financing offered"],
              ].map(([k, l]) => (
                <label key={k} className="flex items-start gap-2 p-2 rounded-sm border border-white/8 cursor-pointer" data-testid={`ve-${k}-wrap`}>
                  <input type="checkbox" checked={f[k]} onChange={(e) => set(k, e.target.checked)} className="mt-1" data-testid={`ve-${k}`} />
                  <span className="text-xs">{l}</span>
                </label>
              ))}
            </div>
            <Field label="Seller financing terms" span={2}>
              <input className={inputCls} value={f.seller_financing_terms} onChange={(e) => set("seller_financing_terms", e.target.value)} data-testid="ve-seller-financing-terms" />
            </Field>
          </Section>
        </div>

        <footer className="flex items-center justify-end gap-2 p-4 border-t border-white/8 sticky bottom-0 bg-[#05070A]">
          <button type="button" onClick={onClose} data-testid="verify-edit-cancel" className="btn-ghost text-sm">Cancel</button>
          <button type="submit" disabled={busy} data-testid="verify-edit-save" className="btn-primary text-sm inline-flex items-center gap-2">
            {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : "Save changes"}
          </button>
        </footer>
      </form>
    </div>
  );
}
